import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { fetchRolesForGuard } from "@/lib/roleGuard";

// Single cached source of truth for the signed-in user's roles.
//
// Before this hook, ~20 components each ran their own
// `select role from user_roles where user_id = ...` in a raw useEffect on
// every mount — the feed's comment component did it once per update card.
// Roles effectively never change mid-session, so one cached query serves
// everyone; role revocation takes effect on the next session, same as before.
const ROLES_STALE_MS = 30 * 60 * 1000;

export function useUserRoles(userId: string | null | undefined) {
  const query = useQuery({
    queryKey: ["user-roles", userId],
    enabled: !!userId,
    staleTime: ROLES_STALE_MS,
    gcTime: ROLES_STALE_MS * 2,
    // A phone waking from the background can miss once while its token
    // refreshes; retrying keeps a guard from mistaking that for no roles.
    retry: 2,
    queryFn: async () => {
      // One shared reader, so a guard here tells a failed lookup apart from
      // an empty one exactly as the dashboards do. It reports to Sentry and
      // verifies an empty answer against the auth server before trusting it.
      const check = await fetchRolesForGuard(userId!);
      if (check.failed) throw new Error("Role guard: the roles lookup failed");
      return check.roles;
    },
  });

  const roles = query.data ?? [];
  return {
    roles,
    rolesLoading: query.isLoading,
    // isFetched turns true after a FAILED fetch too, so a guard that reads it
    // alone will deny someone whose lookup merely errored. rolesAnswered is
    // true only when the roles below are the real answer.
    rolesFetched: query.isFetched,
    rolesAnswered: query.isSuccess,
    rolesFailed: query.isError,
    isAdmin: roles.includes("admin"),
    isTeacher: roles.includes("teacher"),
    isSupport: roles.includes("support"),
    isChafAdmin: roles.includes("chaf_admin"),
    hasRole: (role: string) => roles.includes(role),
  };
}

// Convenience variant for components that don't already track the user id.
// getSession() reads local storage — no network round trip.
export function useCurrentUserRoles() {
  const { data: userId } = useQuery({
    queryKey: ["auth-user-id"],
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const { data } = await supabase.auth.getSession();
      return data.session?.user?.id ?? null;
    },
  });
  return { userId: userId ?? null, ...useUserRoles(userId) };
}
